'use client';

import { useEffect, useState } from 'react';
import { Loader2, Check } from 'lucide-react';

interface LoadingStep {
  id: string;
  label: string;
  status: 'pending' | 'loading' | 'completed' | 'error';
}

interface DetailedLoadingStateProps {
  gemeindeName?: string;
  plz: string;
}

export function DetailedLoadingState({ gemeindeName, plz }: DetailedLoadingStateProps) {
  const [steps, setSteps] = useState<LoadingStep[]>([
    { id: 'gemeinde', label: 'Gemeindedaten laden', status: 'loading' },
    { id: 'solar', label: 'Solaranlagen abrufen', status: 'pending' },
    { id: 'wasser', label: 'Wasserkraftwerke prüfen', status: 'pending' },
    { id: 'biomasse', label: 'Biomasseanlagen suchen', status: 'pending' },
    { id: 'biogas', label: 'Biogasanlagen ermitteln', status: 'pending' },
    { id: 'calculate', label: 'Potenzial berechnen', status: 'pending' },
  ]);

  // Simulate loading progress
  useEffect(() => {
    const timers: NodeJS.Timeout[] = [];
    
    // Update steps progressively
    steps.forEach((step, index) => {
      if (index === 0) {
        // First step is already loading
        timers.push(
          setTimeout(() => {
            setSteps(prev => prev.map(s => 
              s.id === step.id ? { ...s, status: 'completed' } : s
            ));
          }, 500)
        );
      } else {
        // Start loading
        timers.push(
          setTimeout(() => {
            setSteps(prev => prev.map(s => 
              s.id === step.id ? { ...s, status: 'loading' } : s
            ));
          }, 400 * index)
        );
        
        // Complete loading
        timers.push(
          setTimeout(() => {
            setSteps(prev => prev.map(s => 
              s.id === step.id ? { ...s, status: 'completed' } : s
            ));
          }, 400 * index + 400)
        );
      }
    });

    return () => {
      timers.forEach(timer => clearTimeout(timer));
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only once on mount - steps is intentionally excluded

  const getStepIcon = (status: LoadingStep['status']) => {
    switch (status) {
      case 'loading':
        return <Loader2 className="h-4 w-4 animate-spin text-green-600" />;
      case 'completed':
        return <Check className="h-4 w-4 text-green-600" />;
      case 'error':
        return <span className="h-4 w-4 text-red-600">✕</span>;
      default:
        return <span className="h-4 w-4 rounded-full bg-gray-300" />;
    }
  };

  const completedSteps = steps.filter(s => s.status === 'completed').length;
  const progress = (completedSteps / steps.length) * 100;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-white/80 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-xl p-6 w-full mx-4 space-y-4" style={{ maxWidth: '400px' }}>
        {/* Title */}
        <div className="text-center space-y-1">
          <h2 className="text-lg font-semibold text-gray-900">
            Lade Energiedaten
          </h2>
          <p className="text-sm text-gray-600">
            {gemeindeName || `PLZ ${plz}`}
          </p>
        </div>

        {/* Progress bar */}
        <div className="w-full">
          <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
            <div 
              className="h-full bg-green-600 transition-all duration-300 ease-out"
              style={{ width: `${progress}%` }}
            />
          </div>
          <p className="text-center text-xs text-gray-500 mt-1">
            {completedSteps} von {steps.length} Schritten abgeschlossen
          </p>
        </div>

        {/* Detailed steps */}
        <div className="w-full space-y-1.5">
          {steps.map((step) => (
            <div 
              key={step.id}
              className={`flex items-center space-x-2 px-2 py-1.5 rounded-lg transition-all duration-200 ${
                step.status === 'loading' ? 'bg-green-50' : 
                step.status === 'completed' ? 'opacity-70' : 
                'opacity-40'
              }`}
            >
              {getStepIcon(step.status)}
              <span className={`text-sm ${
                step.status === 'loading' ? 'font-medium text-gray-900' : 
                step.status === 'completed' ? 'text-gray-600' : 
                'text-gray-400'
              }`}>
                {step.label}
              </span>
            </div>
          ))}
        </div>

        {/* Fun fact while loading */}
        <div className="w-full text-center p-3 bg-blue-50 rounded-lg">
          <p className="text-xs text-blue-700">
            <strong>Wussten Sie?</strong> Eine durchschnittliche Tiroler Gemeinde 
            kann mit einer Energiegemeinschaft bis zu €250.000 pro Jahr erwirtschaften!
          </p>
        </div>
      </div>
    </div>
  );
}